import { z } from "zod";
import { zId } from "./common.js";
import { zCustomFieldDef, zCustomFieldType } from "./customField.js";
import { zSegmentCondition } from "./segment.js";

/**
 * CT-RM-34: custom objects. An org defines its own record types (scholarships,
 * properties, vehicles, board terms...). Their fields are ordinary custom field
 * definitions (custom_field_defs with object "custom_object" + objectTypeId),
 * so validation, field-level security, segments and imports work the same way
 * they do for constituents and gifts.
 */

/** icon choices for an object type (rendered from the web icon set) */
export const CUSTOM_OBJECT_ICONS = ["box", "award", "home", "calendar", "file", "handshake", "id_card", "star", "flag", "briefcase"] as const;
export const zCustomObjectIcon = z.enum(CUSTOM_OBJECT_ICONS);
export type CustomObjectIcon = z.infer<typeof zCustomObjectIcon>;

/** stable identifier: used in URLs, the API, segment fields (object.<key>) and imports */
export const zCustomObjectKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, "lowercase letters, digits and underscores, starting with a letter")
  .max(40);

export const zCustomObjectType = z.object({
  id: zId,
  key: zCustomObjectKey,
  name: z.string(),
  pluralName: z.string(),
  icon: zCustomObjectIcon,
  description: z.string().nullable(),
  archived: z.boolean(),
  /** custom field definitions targeting this type, in display order */
  fields: z.array(zCustomFieldDef),
  recordCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomObjectType = z.infer<typeof zCustomObjectType>;

export const zCreateCustomObjectType = z.object({
  key: zCustomObjectKey,
  name: z.string().trim().min(1).max(80),
  pluralName: z.string().trim().min(1).max(80),
  icon: zCustomObjectIcon.default("box"),
  description: z.string().trim().max(500).optional(),
});
export type CreateCustomObjectType = z.infer<typeof zCreateCustomObjectType>;

/** the key is immutable once records, segments and API clients depend on it */
export const zUpdateCustomObjectType = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  pluralName: z.string().trim().min(1).max(80).optional(),
  icon: zCustomObjectIcon.optional(),
  description: z.string().trim().max(500).nullable().optional(),
  archived: z.boolean().optional(),
});
export type UpdateCustomObjectType = z.infer<typeof zUpdateCustomObjectType>;

/** a field on an object type — the custom field definition contract without `object` */
export const zCreateCustomObjectField = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/).max(60),
  label: z.string().trim().min(1).max(120),
  type: zCustomFieldType,
  groupName: z.string().trim().max(120).optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(120)).max(200).optional(),
  restrictedToPermission: z.string().optional(),
});
export type CreateCustomObjectField = z.infer<typeof zCreateCustomObjectField>;

export const CUSTOM_OBJECT_ASSOCIATION_TARGETS = ["constituent", "gift"] as const;

export const zCustomObjectAssociation = z.object({
  id: zId,
  recordId: zId,
  target: z.enum(CUSTOM_OBJECT_ASSOCIATION_TARGETS),
  constituentId: zId.nullable(),
  giftId: zId.nullable(),
  /** e.g. "Recipient", "Sponsor" */
  role: z.string().nullable(),
  /** constituent display name, or the gift's donor + amount */
  label: z.string(),
  createdAt: z.string(),
});
export type CustomObjectAssociation = z.infer<typeof zCustomObjectAssociation>;

export const zCustomObjectRecord = z.object({
  id: zId,
  typeId: zId,
  typeKey: zCustomObjectKey,
  name: z.string(),
  customFields: z.record(z.unknown()),
  associationCount: z.number().int(),
  createdById: zId.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomObjectRecord = z.infer<typeof zCustomObjectRecord>;

export const zCustomObjectRecordDetail = zCustomObjectRecord.extend({
  associations: z.array(zCustomObjectAssociation),
});
export type CustomObjectRecordDetail = z.infer<typeof zCustomObjectRecordDetail>;

export const zCreateCustomObjectAssociation = z
  .object({
    constituentId: zId.optional(),
    giftId: zId.optional(),
    role: z.string().trim().max(80).optional(),
  })
  .refine((a) => Boolean(a.constituentId) !== Boolean(a.giftId), { message: "Link exactly one constituent or gift" });
export type CreateCustomObjectAssociation = z.infer<typeof zCreateCustomObjectAssociation>;

export const zCreateCustomObjectRecord = z.object({
  name: z.string().trim().min(1).max(200),
  customFields: z.record(z.unknown()).default({}),
  /** link the new record to these constituents in the same transaction */
  constituentIds: z.array(zId).max(50).optional(),
  role: z.string().trim().max(80).optional(),
});
export type CreateCustomObjectRecord = z.infer<typeof zCreateCustomObjectRecord>;

/** customFields merge: keys present replace, null clears */
export const zUpdateCustomObjectRecord = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  customFields: z.record(z.unknown()).optional(),
});
export type UpdateCustomObjectRecord = z.infer<typeof zUpdateCustomObjectRecord>;

/** record filters reuse the segment condition shape: `name`, `createdAt`, `updatedAt` or `custom.<key>` */
export const zCustomObjectRecordFilter = z.array(zSegmentCondition).max(20);
export type CustomObjectRecordFilter = z.infer<typeof zCustomObjectRecordFilter>;

export const zCustomObjectRecordListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  /** name | createdAt | updatedAt | custom.<key> */
  sort: z
    .string()
    .regex(/^(name|createdAt|updatedAt|custom\.[a-z][a-z0-9_]*)$/)
    .default("name"),
  dir: z.enum(["asc", "desc"]).default("asc"),
  /** JSON-encoded array of segment-style conditions */
  filter: z
    .string()
    .max(8000)
    .optional()
    .transform((raw, ctx) => {
      if (!raw) return undefined;
      try {
        const parsed = zCustomObjectRecordFilter.safeParse(JSON.parse(raw));
        if (parsed.success) return parsed.data;
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: parsed.error.issues.map((i) => i.message).join("; ") });
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "filter must be a JSON array of conditions" });
      }
      return z.NEVER;
    }),
  /** only records linked to this constituent */
  constituentId: zId.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type CustomObjectRecordListQuery = z.infer<typeof zCustomObjectRecordListQuery>;

export const zCustomObjectExportQuery = z.object({
  format: z.enum(["csv", "xlsx"]).default("csv"),
});

/** a constituent's linked records, grouped by type (the ConstituentDetail section) */
export const zConstituentCustomObjects = z.array(
  z.object({
    type: zCustomObjectType.omit({ fields: true, recordCount: true }).extend({ fields: z.array(zCustomFieldDef) }),
    records: z.array(
      zCustomObjectRecord.extend({ associationId: zId, role: z.string().nullable() }),
    ),
  }),
);
export type ConstituentCustomObjects = z.infer<typeof zConstituentCustomObjects>;
